import { Button, Form, Input, Modal, Select } from 'antd';
import { EditUserFormData } from '../../types/user';
import { useAppStore } from '../../store/appStore';
import { useEffect, useState } from 'react';
import PhoneInput from 'react-phone-number-input';
import type { CountryCode } from 'libphonenumber-js';
import 'react-phone-number-input/style.css';
import { User } from '../../types';

interface EditUserProps {
  isEditModalVisible: boolean;
  setIsEditModalVisible: (visible: boolean) => void;
  handleUpdateUser: (values: EditUserFormData) => void;
  initialData: User;
}

const EditUser = ({
  isEditModalVisible,
  setIsEditModalVisible,
  handleUpdateUser,
  initialData,
}: EditUserProps) => {
  const [form] = Form.useForm();
  const [phone, setPhone] = useState<string>('');
  const [defaultCountry, setDefaultCountry] = useState<CountryCode | undefined>(undefined);

  const roles = useAppStore((state) => state.roles);
  const filteredRoles = roles.filter((role:any) => role.roleName !== 'super admin');
  const plants = useAppStore((state) => state.plants);

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        name: initialData.name,
        email: initialData.email,
        roleId: initialData.roleId,
        plantId: initialData.plantId,
      });

      setPhone(initialData.phoneNumber || '');
      setDefaultCountry(initialData.countryCode as CountryCode);
    }
  }, [initialData, form]);

  const handleEditCancel = () => {
    form.resetFields();
    setPhone('');
    setDefaultCountry(undefined);
    setIsEditModalVisible(false);
  };

  const handleEdit = async () => {
    try {
      const values = await form.validateFields();
      if (!phone || !defaultCountry) throw new Error('Phone number or country code missing');

      const payload: EditUserFormData = {
        ...values,
        phoneNumber: phone,
        countryCode: defaultCountry,
      };

      handleUpdateUser(payload);
      form.resetFields();
      setPhone('');
      setDefaultCountry(undefined);
      setIsEditModalVisible(false);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <Modal
      title="Edit User"
      open={isEditModalVisible}
      onCancel={handleEditCancel}
      footer={[
        <Button key="cancel" onClick={handleEditCancel}>
          Cancel
        </Button>,
        <Button key="edit" type="primary" onClick={handleEdit}>
          Save Changes
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" name="editUserForm" className="mt-4">
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Please enter the name' }]}
        >
          <Input placeholder="Enter full name" />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Please enter the email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}
        >
          <Input placeholder="Enter email address" />
        </Form.Item>

        <Form.Item
          label="Phone Number"
          required
          validateStatus={!phone ? 'error' : ''}
          help={!phone ? 'Please enter the phone number' : ''}
        >
          <PhoneInput
            international
            defaultCountry={defaultCountry}
            value={phone}
            onChange={(value) => setPhone(value || '')}
            placeholder="Enter phone number"
            className="custom-phone-input"
          />
        </Form.Item>

        <Form.Item
          name="roleId"
          label="Role"
          rules={[{ required: true, message: 'Please select a role' }]}
        >
          <Select placeholder="Select role">
            {filteredRoles.map((role) => (
              <Select.Option key={role.id} value={role.id}>
                {role.roleName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="plantId"
          label="Plants"
          rules={[{ required: true, message: 'Please select at least one plant' }]}
        >
          <Select placeholder="Select plant">
            {plants.map((plant) => (
              <Select.Option key={plant.id} value={plant.id}>
                {plant.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditUser;
